import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert
} from '@mui/material';
import { 
  Security, 
  Info, 
  ContactMail,
  Update
} from '@mui/icons-material';

const PrivacyPolicy = () => {
  const lastUpdated = "December 14, 2024";

  const sections = [
    {
      title: "Information We Collect",
      content: [
        "Personal identification information (name, email address, phone number)",
        "Property search preferences and saved searches",
        "Payment information for subscription services",
        "Usage data and website analytics",
        "Device information and IP addresses",
        "Communication preferences and history"
      ]
    },
    {
      title: "How We Use Your Information",
      content: [
        "Provide and maintain our services",
        "Process transactions and manage subscriptions",
        "Send property alerts and notifications",
        "Improve our platform and user experience",
        "Communicate with you about updates and features",
        "Comply with legal obligations"
      ]
    },
    {
      title: "Information Sharing",
      content: [
        "We do not sell, trade, or rent your personal information to third parties",
        "We may share anonymized, aggregated data for research purposes",
        "Service providers who assist in operating our platform may access data",
        "Legal authorities may receive information if required by law",
        "Business transfers may include user data in merger or acquisition"
      ]
    },
    {
      title: "Data Security",
      content: [
        "Industry-standard encryption for data transmission",
        "Secure servers with regular security updates",
        "Limited access controls for employee data access",
        "Regular security audits and vulnerability assessments",
        "Incident response procedures for data breaches"
      ]
    },
    {
      title: "Your Rights",
      content: [
        "Access your personal data at any time",
        "Request correction of inaccurate information", 
        "Delete your account and associated data",
        "Export your data in a portable format",
        "Opt-out of marketing communications",
        "Object to certain data processing activities"
      ]
    },
    {
      title: "Cookies and Tracking",
      content: [
        "Essential cookies for platform functionality",
        "Analytics cookies to understand user behavior",
        "Marketing cookies for personalized advertising",
        "Third-party cookies from integrated services",
        "Cookie preferences can be managed in settings"
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 700, 
            color: '#1B5E20',
            mb: 2 
          }}>
            Privacy Policy
          </Typography>
          <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
            Your privacy is important to us. This policy explains how we collect, 
            use, and protect your information.
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            <Update sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
            Last updated: {lastUpdated}
          </Typography>
        </Box>

        {/* Introduction */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body1">
            <strong>DS360</strong> ("we," "our," or "us") is committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your 
            information when you use our foreclosure property platform.
          </Typography>
        </Alert>

        {/* Privacy Sections */}
        {sections.map((section, index) => (
          <Card key={index} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 600, 
                mb: 3, 
                color: '#1B5E20',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Security sx={{ mr: 2, color: '#1B5E20' }} />
                {section.title}
              </Typography>
              <List>
                {section.content.map((item, itemIndex) => (
                  <ListItem key={itemIndex} sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary={item}
                      sx={{ 
                        '& .MuiListItemText-primary': { 
                          color: '#666',
                          fontSize: '1rem'
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        ))}

        {/* GDPR & CCPA Compliance */}
        <Card sx={{ mb: 4, bgcolor: '#e8f5e8' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
              International Privacy Laws
            </Typography>
            
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              GDPR (General Data Protection Regulation)
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
              If you are located in the European Union, you have additional rights under GDPR, 
              including the right to data portability, the right to be forgotten, and the right 
              to object to processing based on legitimate interests.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              CCPA (California Consumer Privacy Act)
            </Typography>
            <Typography variant="body1" sx={{ color: '#666' }}>
              California residents have the right to know what personal information is collected, 
              request deletion of personal information, opt-out of the sale of personal information, 
              and not be discriminated against for exercising these rights.
            </Typography>
          </CardContent>
        </Card>

        {/* Data Retention */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
              Data Retention
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
              We retain your personal information for as long as necessary to provide our services 
              and fulfill the purposes outlined in this privacy policy. Specific retention periods include:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Account data: Retained while account is active plus 3 years" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Transaction records: 7 years for legal compliance" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Marketing data: Until you opt-out or 2 years of inactivity" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Analytics data: Aggregated and anonymized after 26 months" />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card sx={{ bgcolor: '#1B5E20', color: 'white' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <ContactMail sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Questions About Privacy?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              If you have any questions about this Privacy Policy or our data practices, 
              please contact our Data Protection Officer.
            </Typography>
            
            <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.3)' }} />
            
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Email:</strong> connect@ds360investment.com
            </Typography>
            <Typography variant="body1">
              <strong>Phone:</strong> (201) 912-0705
            </Typography>
          </CardContent>
        </Card>

        {/* Policy Updates */}
        <Alert severity="warning" sx={{ mt: 4 }}>
          <Typography variant="body1">
            <strong>Policy Updates:</strong> We may update this Privacy Policy from time to time. 
            We will notify you of any material changes by posting the new Privacy Policy on this page 
            and updating the "Last updated" date. We encourage you to review this Privacy Policy 
            periodically for any changes.
          </Typography>
        </Alert>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;














